import {
  faArrowLeft,
  faArrowRight,
  faTag
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ReactNode, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useApplicant } from '~/api/queries/applicant'
import { useOrder } from '~/api/queries/order'
import { content as contentModalStyle } from '~/components/Global/Design/Modal/Modal.css'
import { HoverInfo } from '~/components/Global/Elements/HoverInfo/HoverInfo'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { Button } from '~/components/Global/Interactions/Button'
import { applicantAccessDeniedAgencies } from '~/domains/applicants/accessDenied'
import { ApplicantAttachmentsViewer } from '~/domains/applicants/components/ApplicantAttachmentsViewer'
import {
  applicantHoverList,
  applicantHoverListItem
} from '~/domains/applicants/components/ApplicantCardHoverContent/ApplicantCardHoverContent.css'
import { ApplicantModalErrorFallback } from '~/domains/applicants/components/ApplicantModal/ApplicantModalErrorFallback'
import { ApplicantModalHeader } from '~/domains/applicants/components/ApplicantModal/ApplicantModalHeader'
import { ApplicantModalLayout } from '~/domains/applicants/components/ApplicantModal/ApplicantModalLayout'
import { ApplicantHistory } from '~/domains/applicants/components/History/ApplicantHistory'
import { useApplicantsHead } from '~/domains/applicants/queries/applicantHead'
import { useApplicantPicture } from '~/domains/applicants/queries/applicantPictures'
import { ApplicantTasks } from '~/domains/tasks/components/ApplicantTasks/ApplicantTasks'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { useKeyboardNavigation } from '~/hooks/useKeyboardNavigation'
import type { ApplicantModalType } from '~/types/global'
import { useCloseAction } from '../../Actions/hooks/useCloseAction'
import {
  applicantModalAside,
  applicantModalMain,
  applicantModalMainHeader,
  applicantModalNavButton,
  applicantModalNavContainer,
  applicantModalNavContainerRight,
  applicantModalWrapper
} from './ApplicantModal.css'
import { ApplicantModalActions } from './components/ApplicantModalActions'
import { ApplicantReports } from './components/ApplicantReports'
import { OrderStatusUpdater } from './components/OrderStatusUpdater'

type ApplicantModalProps = {
  defaultApplicantId: number
  collection?: ApplicantModalType[]
  openNextAction?: boolean
  onClose: () => void
}

export const ApplicantModal = ({
  defaultApplicantId,
  collection,
  openNextAction,
  onClose
}: ApplicantModalProps) => {
  const [localCollection, setLocalCollection] = useState<ApplicantModalType[]>(
    collection ?? []
  )
  const [applicantId, setApplicantId] = useState(defaultApplicantId)
  const currentApplicant = localCollection?.find(
    (applicant) => applicant.id === applicantId
  )
  const {
    data: applicant,
    isLoading,
    failureReason,
    refetch,
    isError
  } = useApplicant({ id: applicantId, showToast: false })
  const isNotFound = failureReason?.httpStatus === 404
  const isForbidden = failureReason?.httpStatus === 403

  const agencies = applicantAccessDeniedAgencies(failureReason)
  const { data: order } = useOrder(currentApplicant?.order?.id)
  const { data: applicantPicture, isLoading: applicantsPictureLoading } =
    useApplicantPicture({ applicantId })
  const closeAction = useCloseAction('modal')
  const currentOrder = currentApplicant?.order

  const collectionData = useApplicantsHead({
    applicantIds: localCollection?.map(({ id }) => id) ?? []
  })

  const onModalClose = () => {
    closeAction().then((confirm) => {
      if (confirm) {
        onClose()
      }
    })
  }

  const updateCurrentApplicantOrderStatusInLocalCollection = (
    status: number
  ) => {
    setLocalCollection((collection) => {
      return [
        ...collection.map((applicant) => {
          if (!applicant.order) {
            return applicant
          }
          if (applicant.id === currentApplicant?.id) {
            return {
              ...applicant,
              order: {
                ...applicant.order,
                status,
                statusUpdatedAt: new Date().toISOString(),
                statusNote: applicant.order.statusNote
              }
            }
          }
          return applicant
        })
      ]
    })
  }

  const previousApplicant =
    localCollection?.[
      localCollection?.findIndex(({ id }) => id === applicantId) - 1
    ]
  const nextApplicant =
    localCollection?.[
      localCollection?.findIndex(({ id }) => id === applicantId) + 1
    ]

  useKeyboardNavigation({
    hasPrev: !!previousApplicant,
    hasNext: !!nextApplicant,
    onPrev: () => previousApplicant && setApplicantId(previousApplicant.id),
    onNext: () => nextApplicant && setApplicantId(nextApplicant.id)
  })

  const ApplicantNavDetails = ({
    applicant: { id, firstname, lastname },
    onClick,
    className,
    children
  }: {
    applicant: { id: number; firstname?: string; lastname?: string }
    onClick?: () => void
    className?: string
    children?: ReactNode
  }) => {
    const applicantData = collectionData.find(
      ({ data }) => data?.id === id
    )?.data
    if (!applicantData && (!firstname || !lastname)) {
      return (
        <Button onClick={onClick} className={className}>
          {children}
        </Button>
      )
    }
    return (
      <Tooltip
        text={`${applicantData?.firstname ?? firstname} ${applicantData?.lastname ?? lastname}`}
        direction="top"
        asChild
      >
        <span
          onClick={onClick}
          className={clsx(applicantModalNavButton, className)}
        >
          {children}
        </span>
      </Tooltip>
    )
  }

  let firstName = currentApplicant?.firstname ?? applicant?.firstname
  let lastName = currentApplicant?.lastname ?? applicant?.lastname
  const collectionApplicant = collectionData.find(
    ({ data }) => data?.id === applicantId
  )?.data
  if (collectionApplicant) {
    firstName = collectionApplicant.firstname
    lastName = collectionApplicant.lastname
  }
  const profilePictureUrl = applicantPicture?.profilePicUrl

  const { data: orderStatuses } = useTaxonomy('applicant_order_statuses', {
    enabled: !!currentOrder?.id
  })

  const isSpecialStatusAccepted =
    orderStatuses?.find((status) => status.id === currentOrder?.status)
      ?.specialStatus === 'accepted'
  const qualificationNames =
    applicant?.qualifications
      ?.map(({ name }) => name)
      .filter((name): name is string => Boolean(name)) ?? []
  const firstQualification = qualificationNames[0]
  const remainingQualificationsCount = Math.max(
    qualificationNames.length - 1,
    0
  )
  const qualificationsTriggerRef = useRef<HTMLSpanElement | null>(null)

  if (isNotFound) {
    return (
      <ApplicantModalErrorFallback
        error={new Error('Candidat non trouvé')}
        resetErrorBoundary={() => {}}
        onClose={onClose}
        errorType="not-found"
      />
    )
  }

  if (isForbidden) {
    return (
      <ApplicantModalErrorFallback
        error={new Error('Accès refusé')}
        resetErrorBoundary={() => {}}
        onClose={onClose}
        errorType="forbidden"
        agencies={agencies}
      />
    )
  }

  if (isError && !isLoading) {
    return (
      <ApplicantModalErrorFallback
        error={
          new Error(
            "Une erreur est survenue lors de l'affichage de ce candidat"
          )
        }
        resetErrorBoundary={() => {
          refetch()
        }}
        onClose={onClose}
        errorType="error"
      />
    )
  }

  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <ApplicantModalErrorFallback {...props} onClose={onClose} />
      )}
      onError={(error, errorInfo) => {
        console.error('Applicant Modal Error:', error, errorInfo)
      }}
    >
      <ApplicantModalLayout onClose={onModalClose}>
        <section className={clsx(contentModalStyle, applicantModalWrapper)}>
          <main className={applicantModalMain}>
            <div className={applicantModalMainHeader}>
              {(localCollection?.length ?? 0) > 1 && (
                <>
                  {previousApplicant && (
                    <ApplicantNavDetails
                      applicant={previousApplicant}
                      onClick={() => setApplicantId(previousApplicant?.id)}
                      className={applicantModalNavContainer}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </ApplicantNavDetails>
                  )}
                  {nextApplicant && (
                    <ApplicantNavDetails
                      applicant={nextApplicant}
                      onClick={() => setApplicantId(nextApplicant?.id)}
                      className={clsx(
                        applicantModalNavContainer,
                        applicantModalNavContainerRight
                      )}
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </ApplicantNavDetails>
                  )}
                </>
              )}
              <ApplicantModalHeader
                fullName={`${firstName ?? ''} ${lastName ?? ''}`.trim()}
                profilePictureUrl={profilePictureUrl}
                isProfilePictureLoading={applicantsPictureLoading}
                isLoading={isLoading}
                phone={applicant?.phoneMobile || applicant?.phoneSecondary}
                role={
                  isLoading ? (
                    <Shimmer height={12} width={100} />
                  ) : firstQualification && remainingQualificationsCount > 0 ? (
                    <>
                      <span ref={qualificationsTriggerRef}>
                        {firstQualification} +{remainingQualificationsCount}
                      </span>
                      <HoverInfo linkedRef={qualificationsTriggerRef.current}>
                        <div className={applicantHoverList}>
                          {qualificationNames.map((qualificationName) => (
                            <div
                              key={qualificationName}
                              className={applicantHoverListItem}
                            >
                              <FontAwesomeIcon icon={faTag} />
                              {qualificationName}
                            </div>
                          ))}
                        </div>
                      </HoverInfo>
                    </>
                  ) : firstQualification ? (
                    <span>{firstQualification}</span>
                  ) : undefined
                }
                applicantId={applicantId}
                applicantStatus={applicant?.status}
                contact={applicant?.contact}
              />
            </div>
            <ApplicantAttachmentsViewer applicantId={applicantId} />
          </main>
          <aside className={applicantModalAside}>
            <ApplicantModalActions
              applicantId={applicantId}
              contextOrder={
                currentOrder?.id
                  ? {
                      id: currentOrder?.id,
                      willChangeStatus: !isSpecialStatusAccepted
                    }
                  : undefined
              }
              hasEmail={applicant ? !!applicant.email : true}
            />
            <ApplicantTasks
              applicantId={applicantId}
              openOnMount={openNextAction}
            />
            {applicant && currentOrder && order && (
              <OrderStatusUpdater
                order={order}
                applicant={applicant}
                applicantStatus={{
                  id: currentOrder.status,
                  updatedAt: currentOrder.statusUpdatedAt,
                  note: currentOrder?.statusNote
                }}
                onStatusChange={
                  updateCurrentApplicantOrderStatusInLocalCollection
                }
              />
            )}
            <ApplicantReports
              applicantId={applicantId}
              key={`applicant-reports-${applicantId}`}
            />
            <ApplicantHistory
              applicantId={applicantId}
              key={`applicant-history-${applicantId}`}
            />
          </aside>
        </section>
      </ApplicantModalLayout>
    </ErrorBoundary>
  )
}
